import React, { useState } from 'react';
import {
	MicrocreditRequest,
	Maybe,
	useSimulateMicrocreditMutation,
	SimulateMicrocreditMutation,
	SimulateMicrocreditInput
} from '@/graphql/__generated__/credit-requests';
import EvaluationMatrix from './MicrocreditSimulation/EvaluationMatrix';
import FinancialIndicators from './MicrocreditSimulation/FinancialIndicators';
import { GRAY_COLORS } from '@/UIlibrary/globalTheme';
import SimulationTerms from './MicrocreditSimulation/SimulationTerms';
import SimulationInputs from './MicrocreditSimulation/SimulationInputs';
import SimulationTable from './MicrocreditSimulation/SimulationTable';
import { useTranslations } from 'use-intl';
import { useSimulationStore } from '@/stores/useSimulationStore';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import Button from '@/UIlibrary/Button/Button';

interface MicrocreditSimulationProps {
	requestData: Maybe<MicrocreditRequest> | undefined;
	refetchData: () => Promise<any>;
}

const MicrocreditSimulation: React.FC<MicrocreditSimulationProps> = ({
	requestData,
	refetchData
}) => {
	const t = useTranslations('MicrocreditRequestSimulation');
	const microcreditsClient = useApolloClient(SCHEMAS.MICROCREDITS);
	const { formData } = useSimulationStore();
	const [formInputErrors, setFormInputErrors] = useState<Record<string, string>>();

	const [simulateMicrocredit, { loading: loadingSimulation }] = useSimulateMicrocreditMutation({
		client: microcreditsClient,
		onCompleted: async (data: SimulateMicrocreditMutation) => {
			const simulationData = data.simulateMicrocredit;
			if (simulationData?.successful) {
				toast.success(t('SimulationSuccess'));
				await refetchData();
			}
			if (simulationData?.mutationErrors && simulationData.mutationErrors.length > 0) {
				Sentry.captureException(simulationData?.mutationErrors, {
					extra: {
						microcreditRequestId: requestData?.microcreditRequestId
					}
				});
				simulationData.mutationErrors.forEach((error) => {
					if (error) {
						switch (error.__typename) {
							case 'InvalidInput':
								setFormInputErrors((prev) => ({
									...prev,
									[error.field]: error.message
								}));
								break;
							case 'InvalidAction':
								toast.error(t('SimulationError'));
								break;
							case 'ActionFailed':
								toast.error(error.message, { duration: 15000 });
								break;
							default:
								Sentry.captureException(new Error(`Unexpected error type: ${error}`), {
									extra: {
										microcreditRequestId: requestData?.microcreditRequestId,
										error
									}
								});
								toast.error(t('SimulationError'));
								break;
						}
					}
				});
				return;
			}
		},
		onError: () => {
			toast.error(t('SimulationError'));
		}
	});

	const handleSimulation = async () => {
		if (!requestData?.microcreditRequestId) {
			toast.error('Missing microcredit request ID');
			return;
		}

		const ensureMoneyFormat = (value: string): string => {
			if (!value) return '0.00 MXN';
			let cleanValue = value;

			// Removing currency symbols and spaces
			cleanValue = cleanValue.replace(/[$MX\s]/g, '');
			// Removing MXN if present
			cleanValue = cleanValue.replace(/MXN/g, '');
			// Removing commas (thousands separators)
			cleanValue = cleanValue.replace(/,/g, '');

			const numValue = parseFloat(cleanValue);
			return isNaN(numValue) ? '0.00 MXN' : `${numValue.toFixed(2)} MXN`;
		};

		const ensureDecimalFormat = (value: string): string => {
			const numValue = parseFloat(value);
			if (isNaN(numValue)) return '0.0';
			return numValue.toFixed(2);
		};

		const input: SimulateMicrocreditInput = {
			microcreditRequestId: requestData.microcreditRequestId,
			amountRequested: ensureMoneyFormat(formData.amountRequested),
			paymentCapacity: ensureMoneyFormat(formData.paymentCapacity),
			daysPerPeriod: formData.daysPerPeriod,
			ordinaryInterestRate: ensureDecimalFormat(formData.ordinaryInterestRate),
			firstPaymentDate: formData.firstPaymentDate,
			sameDayPayment: formData.sameDayPayment,
			percentageOfCreditToSettle: ensureDecimalFormat(formData.percentageOfCreditToSettle)
		};

		console.log('Simulation input:', input); // Debug log

		await simulateMicrocredit({
			variables: { input }
		});
	};

	if (!requestData) {
		return null;
	}

	if (loadingSimulation) {
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="loader"></div>
			</div>
		);
	}

	return (
		<div className="flex flex-col rounded-lg mt-5" style={{ backgroundColor: GRAY_COLORS.gray50 }}>
			<SimulationInputs data={requestData} inputErrors={formInputErrors} />

			<SimulationTerms
				loading={false}
				highestAmountToApprove={
					requestData?.simulation?.configuration?.highestAmountToApprove || undefined
				}
				generatedAt={requestData?.simulation?.generatedAt || undefined}
				minimumPaymentToSettleTheCreditTotal={
					requestData?.simulation?.configuration?.minimumPaymentToSettleTheCreditTotal || undefined
				}
				minimumPaymentToSettleTheCreditPercentage={
					requestData?.simulation?.configuration?.minimumPaymentToSettleTheCreditPercentage ||
					undefined
				}
			/>

			<div className="flex items-center justify-center px-6">
				<div className="flex flex-col gap-8 items-center justify-center pb-4 pt-8 bg-white w-full">
					<div className="w-3/4 flex border-t-2 border-gray-200" />
					<Button
						label={t('DoSimulation')}
						size="auto"
						onClick={handleSimulation}
						disabled={loadingSimulation}
					/>
				</div>
			</div>

			<SimulationTable loading={false} data={requestData.simulation} />

			<FinancialIndicators
				recoveryTimeInYears={requestData?.recoveryTimeInYears}
				economicSolvencyIi={requestData?.economicSolvencyIi}
				businessComprehensiveOperativeEfficiency={
					requestData?.businessComprehensiveOperativeEfficiency
				}
				applicantComprehensiveOperativeEfficiency={
					requestData?.applicantComprehensiveOperativeEfficiency
				}
				calculatedNetIncomeIndex={requestData?.calculatedNetIncomeIndex}
			/>

			<EvaluationMatrix
				requestData={requestData}
				refetchData={refetchData}
				microcreditRequestId={requestData.microcreditRequestId || ''}
			/>
		</div>
	);
};

export default MicrocreditSimulation;
